import axios from 'axios';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
export const API_V1 = `${BACKEND_URL}/api/v1`;

// Real-build client — talks to /api/v1/* (auth required for protected routes).
// Sprint 12: the legacy `/api/*` client (`api`, `fetchJSON`, `postJSON`) was
// removed. The prototype surface is fully retired; all calls go through v1.
export const apiV1 = axios.create({ baseURL: API_V1, withCredentials: true });

// ---- Access-token memory store (NEVER localStorage) ----
let _accessToken = null;
const _listeners = new Set();

export function setAccessToken(t) {
  _accessToken = t || null;
  _listeners.forEach((fn) => {
    try { fn(_accessToken); } catch { /* ignore */ }
  });
}
export function getAccessToken() { return _accessToken; }
export function onTokenChange(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

// Attach access token automatically
apiV1.interceptors.request.use((cfg) => {
  if (_accessToken) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${_accessToken}`;
  }
  return cfg;
});

// 401 → try refresh once; if refresh fails, redirect to /login
let _refreshPromise = null;
apiV1.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    if (
      error.response?.status === 401 &&
      !original._retry &&
      !original.url?.includes('/auth/refresh') &&
      !original.url?.includes('/auth/login') &&
      !original.url?.includes('/auth/logout')
    ) {
      original._retry = true;
      try {
        if (!_refreshPromise) {
          _refreshPromise = apiV1
            .post('/auth/refresh')
            .then((r) => { setAccessToken(r.data.access_token); return r.data.access_token; })
            .finally(() => { _refreshPromise = null; });
        }
        const newTok = await _refreshPromise;
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newTok}` };
        return apiV1.request(original);
      } catch (e) {
        setAccessToken(null);
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          const next = window.location.pathname + window.location.search;
          window.location.assign(`/login?next=${encodeURIComponent(next)}`);
        }
        return Promise.reject(e);
      }
    }
    return Promise.reject(error);
  },
);

// ---- v1 helpers (auth-enforced) ----
export const v1Get = async (path) => (await apiV1.get(path)).data;
export const v1Post = async (path, body) => (await apiV1.post(path, body)).data;
export const v1Patch = async (path, body) => (await apiV1.patch(path, body)).data;
export const v1Put = async (path, body) => (await apiV1.put(path, body)).data;
export const v1Delete = async (path) => { await apiV1.delete(path); };
// Binary read (returns a Blob) — for same-origin bytes such as canvas-safe images.
export const v1GetBlob = async (path) => (await apiV1.get(path, { responseType: 'blob' })).data;

/**
 * Multipart upload to a v1 endpoint.
 * Pass a single `file` (File/Blob) or an object map of additional form fields.
 */
export const v1Upload = async (path, file, extraFields = {}) => {
  const form = new FormData();
  form.append('file', file);
  Object.entries(extraFields).forEach(([k, v]) => form.append(k, v));
  const res = await apiV1.post(path, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

/**
 * Multipart POST with an arbitrary FormData payload (multi-file uploads).
 * Default timeout is generous for large panel recordings.
 */
export const v1UploadForm = async (path, formData, { timeoutMs = 10 * 60 * 1000 } = {}) => {
  const res = await apiV1.post(path, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: timeoutMs,
  });
  return res.data;
};
